<script>
import { useVuelidate } from '@vuelidate/core';
import { required, between } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import LinkedinAPI from 'dashboard/api/linkedin';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import RiskAcceptanceModal from './linkedin/RiskAcceptanceModal.vue';
import router from '../../../../index';

const DEFAULT_CONVERSATIONS = 20;
const DEFAULT_DAYS = 30;
const MAX_CONVERSATIONS = 200;
const MAX_DAYS = 90;
const DEFAULT_MESSAGES_PER_CONVERSATION = 100;
const MAX_MESSAGES_PER_CONVERSATION = 1000;

export default {
  name: 'LinkedinSetup',
  components: {
    LoadingState,
    PageHeader,
    RiskAcceptanceModal,
  },
  setup() {
    const { accountId } = useAccount();
    return {
      accountId,
      v$: useVuelidate(),
      maxConversations: MAX_CONVERSATIONS,
      maxDays: MAX_DAYS,
      maxMessagesPerConversation: MAX_MESSAGES_PER_CONVERSATION,
    };
  },
  data() {
    return {
      inboxName: '',
      importMode: 'count',
      importConversations: DEFAULT_CONVERSATIONS,
      importDays: DEFAULT_DAYS,
      importMessagesPerConversation: DEFAULT_MESSAGES_PER_CONVERSATION,
      historyMessagesPerConversation: DEFAULT_MESSAGES_PER_CONVERSATION,
      importPreexistingHistory: false,
      isRiskModalOpen: false,
      isRequestingLink: false,
      isCreating: false,
      callbackFailed: false,
    };
  },
  validations() {
    const rules = { inboxName: { required } };
    if (this.importMode === 'count') {
      rules.importConversations = {
        required,
        between: between(1, MAX_CONVERSATIONS),
      };
    }
    if (this.importMode === 'time') {
      rules.importDays = { required, between: between(1, MAX_DAYS) };
    }
    if (this.importMode !== 'none') {
      rules.importMessagesPerConversation = {
        required,
        between: between(1, MAX_MESSAGES_PER_CONVERSATION),
      };
    }
    if (this.importPreexistingHistory) {
      rules.historyMessagesPerConversation = {
        required,
        between: between(1, MAX_MESSAGES_PER_CONVERSATION),
      };
    }
    return rules;
  },
  computed: {
    account() {
      return this.$store.getters['accounts/getAccount'](this.accountId) || {};
    },
    showTimezoneWarning() {
      return !this.account.timezone;
    },
    isCountMode() {
      return this.importMode === 'count';
    },
    isTimeMode() {
      return this.importMode === 'time';
    },
    showMessagesPerConversation() {
      return this.importMode !== 'none';
    },
    hasImportError() {
      return Boolean(
        this.v$.importConversations?.$error ||
          this.v$.importDays?.$error ||
          this.v$.importMessagesPerConversation?.$error
      );
    },
    hasHistoryError() {
      return Boolean(this.v$.historyMessagesPerConversation?.$error);
    },
  },
  mounted() {
    this.handleCallback();
  },
  methods: {
    handleCallback() {
      const params = new URLSearchParams(window.location.search);
      const unipileAccountId = params.get('unipile_account_id');
      const state = params.get('state');
      const hasError = params.has('linkedin_error');
      if (!hasError && !(unipileAccountId && state)) return;

      window.history.replaceState({}, document.title, window.location.pathname);

      if (hasError || !unipileAccountId || !state) {
        this.callbackFailed = true;
        useAlert(this.$t('INBOX_MGMT.ADD.LINKEDIN.ERRORS.CALLBACK'));
        return;
      }
      this.createChannel({ unipileAccountId, state });
    },
    errorMessage(error, fallbackKey) {
      return error?.response?.data?.error || this.$t(fallbackKey);
    },
    async createChannel(params) {
      this.isCreating = true;
      try {
        const data = await this.$store.dispatch(
          'inboxes/createLinkedinChannel',
          params
        );
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.id },
        });
      } catch (error) {
        this.isCreating = false;
        this.callbackFailed = true;
        useAlert(
          this.errorMessage(error, 'INBOX_MGMT.ADD.LINKEDIN.ERRORS.CONNECT')
        );
      }
    },
    openRiskModal() {
      this.v$.$touch();
      if (this.v$.$invalid) return;
      this.isRiskModalOpen = true;
    },
    closeRiskModal() {
      this.isRiskModalOpen = false;
    },
    buildAuthLinkParams() {
      const params = {
        inbox_name: this.inboxName,
        risk_accepted: true,
        initial_import_mode: this.importMode,
        import_preexisting_history: this.importPreexistingHistory,
      };
      if (this.isTimeMode) params.initial_import_days = Number(this.importDays);
      if (this.isCountMode) {
        params.initial_import_conversations = Number(this.importConversations);
      }
      if (this.showMessagesPerConversation) {
        params.initial_import_messages_per_conversation = Number(
          this.importMessagesPerConversation
        );
      }
      if (this.importPreexistingHistory) {
        params.history_messages_per_conversation = Number(
          this.historyMessagesPerConversation
        );
      }
      return params;
    },
    async confirmRisk() {
      if (this.isRequestingLink) return;
      this.isRequestingLink = true;
      try {
        const { data } = await LinkedinAPI.authLink(this.buildAuthLinkParams());
        window.location.href = data.url;
      } catch (error) {
        this.isRequestingLink = false;
        this.isRiskModalOpen = false;
        useAlert(
          this.errorMessage(error, 'INBOX_MGMT.ADD.LINKEDIN.ERRORS.AUTH_LINK')
        );
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 min-h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0 self-start"
  >
    <LoadingState
      v-if="isCreating"
      :message="$t('INBOX_MGMT.ADD.LINKEDIN.CONNECTING')"
    />

    <div v-else class="max-w-lg mx-auto pt-6 pb-6">
      <img
        src="/assets/images/dashboard/channels/linkedin.svg"
        alt="LinkedIn"
        class="w-16 h-16 mb-5 mx-auto"
      />

      <PageHeader
        class="text-center items-center"
        :header-title="$t('INBOX_MGMT.ADD.LINKEDIN.TITLE')"
        :header-content="$t('INBOX_MGMT.ADD.LINKEDIN.DESC')"
      />

      <div
        v-if="callbackFailed"
        class="mt-4 p-3 text-sm rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
        data-test-id="linkedin-callback-error"
      >
        {{ $t('INBOX_MGMT.ADD.LINKEDIN.ERRORS.CALLBACK') }}
      </div>

      <div
        v-if="showTimezoneWarning"
        class="mt-4 p-3 text-sm rounded-lg bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200"
        data-test-id="linkedin-timezone-warning"
      >
        {{ $t('INBOX_MGMT.ADD.LINKEDIN.TIMEZONE_WARNING') }}
      </div>

      <form class="mt-6" @submit.prevent="openRiskModal">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.LINKEDIN.INBOX_NAME') }}
          <input
            v-model.trim="inboxName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.LINKEDIN.INBOX_NAME_PLACEHOLDER')"
            @input="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.LINKEDIN.INBOX_NAME_REQUIRED') }}
          </span>
        </label>

        <fieldset class="mt-4">
          <legend class="text-sm font-medium mb-2">
            {{ $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.LABEL') }}
          </legend>

          <label
            for="linkedin-import-mode-count"
            class="flex items-center gap-2 mb-2"
          >
            <input
              id="linkedin-import-mode-count"
              v-model="importMode"
              type="radio"
              value="count"
            />
            <span>{{ $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.MODE_COUNT') }}</span>
          </label>
          <div v-if="isCountMode" class="flex items-center gap-2 mb-2 ml-6">
            <input
              id="linkedin-import-conversations"
              v-model.number="importConversations"
              type="number"
              min="1"
              :max="maxConversations"
              class="!w-24 !mb-0"
              data-test-id="linkedin-import-conversations"
            />
            <label for="linkedin-import-conversations">{{
              $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.CONVERSATIONS')
            }}</label>
          </div>

          <label
            for="linkedin-import-mode-time"
            class="flex items-center gap-2 mb-2"
          >
            <input
              id="linkedin-import-mode-time"
              v-model="importMode"
              type="radio"
              value="time"
            />
            <span>{{ $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.MODE_TIME') }}</span>
          </label>
          <div v-if="isTimeMode" class="flex items-center gap-2 mb-2 ml-6">
            <input
              id="linkedin-import-days"
              v-model.number="importDays"
              type="number"
              min="1"
              :max="maxDays"
              class="!w-24 !mb-0"
              data-test-id="linkedin-import-days"
            />
            <label for="linkedin-import-days">{{
              $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.DAYS')
            }}</label>
          </div>

          <label class="flex items-center gap-2 mb-2">
            <input v-model="importMode" type="radio" value="none" />
            <span>{{ $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.MODE_NONE') }}</span>
          </label>

          <div v-if="showMessagesPerConversation" class="mt-2 mb-2">
            <label for="linkedin-import-messages-per-conversation">{{
              $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.MESSAGES_PER_CONVERSATION')
            }}</label>
            <input
              id="linkedin-import-messages-per-conversation"
              v-model.number="importMessagesPerConversation"
              type="number"
              min="1"
              :max="maxMessagesPerConversation"
              class="!w-24 !mb-1"
              data-test-id="linkedin-import-messages-per-conversation"
            />
            <p class="text-xs text-slate-600 dark:text-slate-300">
              {{
                $t(
                  'INBOX_MGMT.ADD.LINKEDIN.IMPORT.MESSAGES_PER_CONVERSATION_HINT'
                )
              }}
            </p>
          </div>

          <p class="text-xs text-slate-600 dark:text-slate-300">
            {{ $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.HINT') }}
          </p>
          <p v-if="hasImportError" class="text-xs text-red-600">
            {{ $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.RANGE_ERROR') }}
          </p>
        </fieldset>

        <label class="flex items-start gap-2 mt-4 cursor-pointer">
          <input
            v-model="importPreexistingHistory"
            type="checkbox"
            class="mt-1"
            data-test-id="linkedin-history-toggle"
          />
          <span>
            {{ $t('INBOX_MGMT.ADD.LINKEDIN.HISTORY_TOGGLE.LABEL') }}
            <span class="block text-xs text-slate-600 dark:text-slate-300">
              {{ $t('INBOX_MGMT.ADD.LINKEDIN.HISTORY_TOGGLE.HINT') }}
            </span>
          </span>
        </label>

        <div class="mt-2 ml-6">
          <label for="linkedin-history-messages-per-conversation">{{
            $t(
              'INBOX_MGMT.ADD.LINKEDIN.HISTORY_TOGGLE.MESSAGES_PER_CONVERSATION'
            )
          }}</label>
          <input
            id="linkedin-history-messages-per-conversation"
            v-model.number="historyMessagesPerConversation"
            type="number"
            min="1"
            :max="maxMessagesPerConversation"
            :disabled="!importPreexistingHistory"
            class="!w-24 !mb-1"
            data-test-id="linkedin-history-messages-per-conversation"
          />
          <p class="text-xs text-slate-600 dark:text-slate-300">
            {{
              $t(
                'INBOX_MGMT.ADD.LINKEDIN.HISTORY_TOGGLE.MESSAGES_PER_CONVERSATION_HINT'
              )
            }}
          </p>
          <p v-if="hasHistoryError" class="text-xs text-red-600">
            {{ $t('INBOX_MGMT.ADD.LINKEDIN.IMPORT.RANGE_ERROR') }}
          </p>
        </div>

        <div class="mt-6">
          <woot-button type="submit" icon="add" data-test-id="linkedin-connect">
            {{ $t('INBOX_MGMT.ADD.LINKEDIN.CONNECT_BUTTON') }}
          </woot-button>
        </div>
      </form>
    </div>

    <RiskAcceptanceModal
      :show="isRiskModalOpen"
      :is-loading="isRequestingLink"
      @confirm="confirmRisk"
      @cancel="closeRiskModal"
    />
  </div>
</template>
